import { useState } from 'react';
import { reservationsApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { toast } from 'sonner';
import { Footprints } from 'lucide-react';

const WalkInDialog = ({ open, onOpenChange, tables, onComplete }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    party_size: 2,
    table_id: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const availableTables = tables.filter(t => 
    t.status === 'available' && t.capacity >= formData.party_size
  ).sort((a, b) => a.capacity - b.capacity);

  const handleSubmit = async () => {
    if (!formData.customer_name.trim()) {
      toast.error('Guest name is required');
      return;
    }
    setSubmitting(true);
    try {
      const data = {
        customer_name: formData.customer_name,
        party_size: formData.party_size,
        table_id: formData.table_id || null,
        notes: formData.notes,
      };
      await reservationsApi.walkIn(data);
      toast.success(`Walk-in seated: ${formData.customer_name} (${formData.party_size})`);
      setFormData({ customer_name: '', party_size: 2, table_id: '', notes: '' });
      onOpenChange(false);
      onComplete?.();
    } catch {
      toast.error('Failed to add walk-in');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="confirm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Footprints size={18} />
            Walk-in
          </DialogTitle>
          <DialogDescription>Quick-seat a walk-in guest</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Guest Name *</Label>
            <Input
              placeholder="Name"
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              autoFocus
              data-testid="walkin-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Party Size</Label>
              <Select
                value={formData.party_size.toString()}
                onValueChange={(v) => setFormData({ ...formData, party_size: parseInt(v), table_id: '' })}
              >
                <SelectTrigger data-testid="walkin-party-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6,7,8,10,12].map(n => (
                    <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Table</Label>
              <Select
                value={formData.table_id}
                onValueChange={(v) => setFormData({ ...formData, table_id: v })}
              >
                <SelectTrigger data-testid="walkin-table-select">
                  <SelectValue placeholder="Auto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No table yet</SelectItem>
                  {availableTables.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} ({t.capacity})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Input
              placeholder="Optional notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting}
            className="bg-verlo hover:bg-verlo-hover"
            data-testid="confirm-walkin-btn"
          >
            {submitting ? 'Seating...' : 'Seat Walk-in'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WalkInDialog;
